import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { apiUrl } from "../../../utils/api";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import { vorgangService } from "../../../services/vorgangService";
import {
    applyKontaktSelection,
    applyKontaktMultiSelection,
    applyRisikoMultiSelection,
    applyVertragMultiSelection,
    kontaktAdresse,
    kontaktLabel,
    risikoLabel,
    vertragLabel,
    type CascadeResult,
    type KontaktRef,
    type LinkSlots,
    type RisikoRef,
    type VertragRef,
} from "./linkSlots";
import {
    contactRefFromRow,
    fetchJson,
    fmtCurrency,
    risikoRefFromRow,
    riskObjectText,
    selectedMap,
    vertragRefFromRow,
} from "./linkFetch";
import "./vorgangDetail.css";
import { alleDokumente } from "../../windowManager/browserFenster/fensterRegister";

type SlotKind = "kontakt" | "risiko" | "vertrag";

/**
 * Der Auswahl-Dialog für Kontakt/Risiko/Vertrag.
 *
 * Exportiert, seit die To-do-Anlage ihn OHNE die volle `LinkRow` braucht: Dort
 * hängt man nur noch über ein „⋯"-Menü ein Risiko oder einen Vertrag an, die
 * drei fest sichtbaren Slots sind weg. Der Dialog selbst bleibt derselbe —
 * zwei Auswahlmasken nebeneinander wären zwei Orte, an denen jede künftige
 * Änderung passieren müsste.
 */
export const LinkSelectionDialog: React.FC<{
    kind: SlotKind;
    slots: LinkSlots;
    onClose: () => void;
    onKontakt?: (kontakt: KontaktRef) => void;
    onKontakte?: (kontakte: KontaktRef[]) => void;
    onRisiken: (risiken: RisikoRef[]) => void;
    onVertraege: (vertraege: VertragRef[]) => void;
}> = ({ kind, slots, onClose, onKontakt, onKontakte, onRisiken, onVertraege }) => {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState<KontaktRef[]>([]);
    const [selectedContacts, setSelectedContacts] = useState<KontaktRef[]>(() =>
        slots.kontakte?.length ? slots.kontakte : slots.kontakt ? [slots.kontakt] : [],
    );
    const [risks, setRisks] = useState<RisikoRef[]>([]);
    const [contracts, setContracts] = useState<VertragRef[]>([]);
    const [selectedRisks, setSelectedRisks] = useState<RisikoRef[]>(() => slots.risiken);
    const [selectedContracts, setSelectedContracts] = useState<VertragRef[]>(() => slots.vertraege);
    const debounced = useDebouncedValue(query, 250);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        let cancelled = false;
        const q = debounced.trim();
        setLoading(true);

        const run = async () => {
            if (kind === "kontakt") {
                let rows: any[] = [];
                if (q.length >= 2) {
                    rows = await fetchJson(apiUrl("contact", { search: q, limit: "30", include: "group" }));
                } else {
                    const filters: Record<string, string[]> = {};
                    if (slots.kontakt?.boTeam?.id) filters.boTeamIds = [slots.kontakt.boTeam.id];
                    if (slots.kontakt?.foUser?.userId) filters.foUserIds = [slots.kontakt.foUser.userId];
                    if (Object.keys(filters).length) {
                        rows = await fetchJson(apiUrl("contact", {
                            limit: "5",
                            include: "group",
                            filters: JSON.stringify(filters),
                        }));
                    }
                }
                if (!cancelled) setContacts(rows.map(contactRefFromRow).filter((c) => Number.isFinite(c.id)));
            } else if (kind === "risiko") {
                let rows: any[] = [];
                if (q.length >= 2) rows = await fetchJson(apiUrl("risk-objects", { q, limit: "50" }));
                else if (slots.kontakt) rows = await fetchJson(apiUrl(`risk-objects/by-contact/${slots.kontakt.id}`));
                if (!cancelled) setRisks(rows.map(risikoRefFromRow).filter((r) => Number.isFinite(r.id)));
            } else {
                let rows: any[] = [];
                if (q.length >= 2) rows = await fetchJson(apiUrl("contracts", { search: q, limit: "50" }));
                else if (slots.risiko) rows = await fetchJson(apiUrl(`contracts/by-risk/${slots.risiko.id}`));
                else if (slots.kontakt) rows = await fetchJson(apiUrl(`contracts/by-contact/${slots.kontakt.id}`));
                if (!cancelled) setContracts(rows.map(vertragRefFromRow).filter((v) => Number.isFinite(v.id)));
            }
            if (!cancelled) setLoading(false);
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [debounced, kind, slots.kontakt, slots.risiko]);

    const selectedRiskIds = useMemo(() => new Set(selectedRisks.map((r) => r.id)), [selectedRisks]);
    const selectedContractIds = useMemo(() => new Set(selectedContracts.map((v) => v.id)), [selectedContracts]);
    const selectedContactIds = useMemo(() => new Set(selectedContacts.map((c) => c.id)), [selectedContacts]);
    const visibleContacts = useMemo(() => {
        const byId = selectedMap(selectedContacts);
        for (const contact of contacts) byId.set(contact.id, contact);
        return Array.from(byId.values());
    }, [contacts, selectedContacts]);

    // PO 28.09.2026: auch der erste Kontakt ist abwählbar — „führend" gibt es
    // nicht mehr (siehe applyKontaktMultiSelection).
    const toggleContact = (contact: KontaktRef) => {
        setSelectedContacts((prev) => {
            const byId = selectedMap(prev);
            if (byId.has(contact.id)) byId.delete(contact.id);
            else byId.set(contact.id, contact);
            return Array.from(byId.values());
        });
    };

    const toggleRisk = (risk: RisikoRef) => {
        setSelectedRisks((prev) => {
            const byId = selectedMap(prev);
            if (byId.has(risk.id)) byId.delete(risk.id);
            else byId.set(risk.id, risk);
            return Array.from(byId.values());
        });
    };

    const toggleContract = (contract: VertragRef) => {
        setSelectedContracts((prev) => {
            const byId = selectedMap(prev);
            if (byId.has(contract.id)) byId.delete(contract.id);
            else byId.set(contract.id, contract);
            return Array.from(byId.values());
        });
    };

    const title =
        kind === "kontakt"
            ? "Kontakte zuordnen"
            : kind === "risiko"
              ? "Risiken zuordnen"
              : "Verträge zuordnen";
    const placeholder =
        kind === "kontakt"
            ? "Kontakt suchen…"
            : kind === "risiko"
              ? "Kennzeichen, Adresse oder Risikoart suchen…"
              : "Vertragsnummer, Risikoobjekt oder Sparte suchen…";
    const empty =
        !debounced.trim() && kind === "kontakt"
            ? "Keine Vorschläge aus der aktuellen Verantwortlichkeit."
            : !debounced.trim() && kind !== "kontakt" && !slots.kontakt && !slots.risiko
              ? "Suchbegriff eingeben."
              : "Keine Treffer.";

    return createPortal(
        <div className="vgd vgd-linkdialog-backdrop" role="presentation" onPointerDown={onClose}>
            <div
                className="vgd-linkdialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="vgd-linkdialog-title"
                onPointerDown={(e) => e.stopPropagation()}
            >
                <div className="vgd-linkdialog-head">
                    <div>
                        <div className="vgd-linkdialog-eyebrow">Zuordnung</div>
                        <h2 id="vgd-linkdialog-title">{title}</h2>
                    </div>
                    <button type="button" className="vgd-linkdialog-close" onClick={onClose} aria-label="Schließen">
                        ×
                    </button>
                </div>

                <input
                    className="vgd-linkdialog-search"
                    autoFocus
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />

                {kind === "kontakt" && !query.trim() && (
                    <div className="vgd-linkdialog-hint">
                        Vorschläge aus der aktuellen FO/BO-Verantwortlichkeit. Suche durchsucht alle sichtbaren Kontakte.
                    </div>
                )}

                <div className="vgd-linkdialog-list">
                    {loading ? (
                        <div className="vgd-linkdialog-empty">Laden…</div>
                    ) : kind === "kontakt" ? (
                        visibleContacts.length ? (
                            visibleContacts.map((c) => {
                                return (
                                    <label key={c.id} className="vgd-linkdialog-row checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedContactIds.has(c.id)}
                                            onChange={() => toggleContact(c)}
                                        />
                                        <span className="vgd-linkdialog-main">{kontaktLabel(c)}</span>
                                        {kontaktAdresse(c) && <span className="vgd-linkdialog-sub">{kontaktAdresse(c)}</span>}
                                    </label>
                                );
                            })
                        ) : (
                            <div className="vgd-linkdialog-empty">{empty}</div>
                        )
                    ) : kind === "risiko" ? (
                        risks.length ? (
                            risks.map((r) => (
                                <label key={r.id} className="vgd-linkdialog-row checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedRiskIds.has(r.id)}
                                        onChange={() => toggleRisk(r)}
                                    />
                                    <span className="vgd-minibadge">{r.riskType || "Risiko"}</span>
                                    <span className="vgd-linkdialog-main">{risikoLabel(r)}</span>
                                    {r.riskObjectIdentifier && <span className="vgd-linkdialog-sub">{r.riskObjectIdentifier}</span>}
                                    {r.contractNbr && <span className="vgd-linkdialog-sub">Vertrag {r.contractNbr}</span>}
                                </label>
                            ))
                        ) : (
                            <div className="vgd-linkdialog-empty">{empty}</div>
                        )
                    ) : contracts.length ? (
                        contracts.map((v) => {
                            const riskText = riskObjectText(v);
                            const premium = fmtCurrency(v.premiumAnnualGross);
                            return (
                                <label key={v.id} className="vgd-linkdialog-row checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedContractIds.has(v.id)}
                                        onChange={() => toggleContract(v)}
                                    />
                                    <span className="vgd-linkdialog-main">{vertragLabel(v)}</span>
                                    {riskText && <span className="vgd-linkdialog-sub">{riskText}</span>}
                                    {v.branch && <span className="vgd-minibadge">{v.branch}</span>}
                                    {premium && <span className="vgd-linkdialog-money">{premium}</span>}
                                </label>
                            );
                        })
                    ) : (
                        <div className="vgd-linkdialog-empty">{empty}</div>
                    )}
                </div>

                <div className="vgd-linkdialog-foot">
                    <span className="vgd-linkdialog-count">
                        {kind === "kontakt"
                            ? selectedContacts.length
                            : kind === "risiko"
                              ? selectedRisks.length
                              : selectedContracts.length} ausgewählt
                    </span>
                    <button type="button" className="vgd-btn" onClick={onClose}>
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        className="vgd-btn primary"
                        onClick={() => {
                            if (kind === "kontakt") {
                                if (onKontakte) onKontakte(selectedContacts);
                                else if (selectedContacts[0] && onKontakt) onKontakt(selectedContacts[0]);
                            } else if (kind === "risiko") onRisiken(selectedRisks);
                            else onVertraege(selectedContracts);
                        }}
                    >
                        Übernehmen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

type Props = {
    slots: LinkSlots;
    editable: boolean;
    /** Seite persistiert den Patch (PUT) und übernimmt result.slots in den State. */
    onApply: (result: CascadeResult) => void | Promise<void>;
    /** Kompakte Darstellung (im ausgeklappten Todo). */
    compact?: boolean;
    /**
     * Vertikales Layout (Light-Erstell-Popup, Mail→Vorgang SP3): Kontakt,
     * Risiko und Vertrag UNTEREINANDER. Kaskadenlogik identisch zur
     * Zeilen-Darstellung.
     */
    vertical?: boolean;
    /**
     * Zeigt unter dem Kontaktnamen die Standard-Anschrift (Cluster VORGANG:
     * Kopfkarte des einschrittigen Mail-Wegs — „Name UND Anschrift").
     *
     * Bewusst optional mit Default `false`: `LinkRow` ist geteilt (Vorgang-
     * Detail, Todo-Detail, Erstell-Flow) und bleibt dort pixelgleich.
     */
    zeigeKontaktAdresse?: boolean;
};

export const LinkRow: React.FC<Props> = ({
    slots,
    editable,
    onApply,
    compact = false,
    vertical = false,
    zeigeKontaktAdresse = false,
}) => {
    const navigate = useNavigate();
    const [dialog, setDialog] = useState<SlotKind | null>(null);

    /** Leere Patches (z. B. unveränderte Auswahl) lösen keinen PUT aus. */
    const apply = (result: CascadeResult): void => {
        if (!Object.keys(result.patch).length) return;
        void onApply(result);
    };

    const pickKontakt = (kontakt: KontaktRef) => {
        setDialog(null);
        apply(applyKontaktSelection(slots, kontakt));
    };

    const pickKontakte = (kontakte: KontaktRef[]) => {
        setDialog(null);
        apply(applyKontaktMultiSelection(slots, kontakte));
    };

    const pickRisiken = async (risiken: RisikoRef[]) => {
        setDialog(null);
        let kontakt: KontaktRef | null = null;
        if (!slots.kontakt && risiken[0]) {
            try {
                const resolved = await vorgangService.resolveLink({ riskObjectId: risiken[0].id });
                kontakt = resolved.kontakt;
            } catch (err) {
                console.error("resolve-link (risiko) fehlgeschlagen", err);
            }
        }
        apply(applyRisikoMultiSelection(slots, risiken, kontakt));
    };

    const pickVertraege = async (vertraege: VertragRef[]) => {
        setDialog(null);
        let resolved: { kontakt?: KontaktRef | null; risiko?: RisikoRef | null } | undefined;
        if (vertraege[0] && (!slots.kontakt || !slots.risiko)) {
            try {
                const res = await vorgangService.resolveLink({ contractId: vertraege[0].id });
                resolved = { kontakt: res.kontakt, risiko: res.risiko };
            } catch (err) {
                console.error("resolve-link (vertrag) fehlgeschlagen", err);
            }
        }
        apply(applyVertragMultiSelection(slots, vertraege, resolved));
    };

    const renderMultiSlot = (kind: "risiko" | "vertrag") => {
        const items: Array<RisikoRef | VertragRef> = kind === "risiko" ? slots.risiken : slots.vertraege;
        const main = items[0];
        const mainLabel = kind === "risiko" ? risikoLabel(main as RisikoRef) : vertragLabel(main as VertragRef);
        const basePath = kind === "risiko" ? "/risks" : "/contracts";
        return (
            <div className="vgd-field vgd-krv-slot" key={kind}>
                <span className="vgd-label">{kind === "risiko" ? "Risiko" : "Vertrag"}</span>
                <span className="vgd-value vgd-krv-val">
                    <button
                        type="button"
                        className="vgd-linkval vgd-krv-name"
                        onClick={() => navigate(`${basePath}/${main.id}`)}
                    >
                        {mainLabel}
                    </button>
                    {items.length > 1 && (
                        <button
                            type="button"
                            className="vgd-morebadge"
                            title="Alle Zuordnungen ansehen/bearbeiten"
                            onClick={() => editable && setDialog(kind)}
                        >
                            +{items.length - 1}
                        </button>
                    )}
                </span>
                {editable && (
                    <button type="button" className="vgd-changelink" onClick={() => setDialog(kind)}>
                        ändern
                    </button>
                )}
            </div>
        );
    };

    const renderAddSlot = (kind: "risiko" | "vertrag") => (
        <div className="vgd-krv-addslot" key={kind}>
            <button type="button" className="vgd-addlink" onClick={() => setDialog(kind)}>
                + {kind === "risiko" ? "Risiko" : "Vertrag"} hinzufügen
            </button>
        </div>
    );

    return (
        <div className={`vgd vgd-krvrow${compact ? " compact" : ""}${vertical ? " vertical" : ""}`}>
            <div className="vgd-field vgd-krv-slot">
                <span className="vgd-label">Kontakt</span>
                {slots.kontakt ? (
                    <>
                        <button
                            type="button"
                            className="vgd-linkval vgd-krv-name"
                            onClick={() => navigate(`/contacts/${slots.kontakt!.id}`)}
                        >
                            {kontaktLabel(slots.kontakt)}
                        </button>
                        {/* Cluster VORGANG: Anschrift unter dem Namen — nur wo der
                            Aufrufer sie ausdrücklich anfordert. Ohne hinterlegte
                            Adresse bleibt die Zeile weg. */}
                        {zeigeKontaktAdresse && kontaktAdresse(slots.kontakt) && (
                            <span className="vgd-krv-adresse">{kontaktAdresse(slots.kontakt)}</span>
                        )}
                        {editable && (
                            <button type="button" className="vgd-changelink" onClick={() => setDialog("kontakt")}>
                                ändern
                            </button>
                        )}
                    </>
                ) : editable ? (
                    <button type="button" className="vgd-addlink" onClick={() => setDialog("kontakt")}>
                        + Kontakt wählen
                    </button>
                ) : (
                    <span style={{ color: "var(--vgd-ink-3)" }}>—</span>
                )}
            </div>

            {slots.risiken.length > 0 ? renderMultiSlot("risiko") : editable ? renderAddSlot("risiko") : null}
            {slots.vertraege.length > 0 ? renderMultiSlot("vertrag") : editable ? renderAddSlot("vertrag") : null}

            {dialog && (
                <LinkSelectionDialog
                    kind={dialog}
                    slots={slots}
                    onClose={() => setDialog(null)}
                    onKontakt={pickKontakt}
                    onKontakte={pickKontakte}
                    onRisiken={(risiken) => void pickRisiken(risiken)}
                    onVertraege={(vertraege) => void pickVertraege(vertraege)}
                />
            )}
        </div>
    );
};
